import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../lib/api';
import ProductCard from '../components/ProductCard';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ChevronRight, HelpCircle } from 'lucide-react';

export default function CategoryPage() {
  const { slug } = useParams();
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [basketFaqs, setBasketFaqs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/categories/${slug}`).then(r => setCategory(r.data)),
      api.get(`/products?category=${slug}`).then(r => setProducts(r.data || [])),
      // Category-specific basket FAQ (from site_settings.faq_basket_<slug>)
      api.get(`/settings/faq_basket_${slug}`).then(r => setBasketFaqs(Array.isArray(r.data) ? r.data : [])).catch(() => setBasketFaqs([])),
    ]).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!category) return <div className="min-h-screen flex items-center justify-center"><p className="text-muted-foreground">Category not found</p></div>;

  return (
    <div data-testid="category-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-8">
        <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
        <ChevronRight className="h-3.5 w-3.5" />
        <span className="text-foreground font-medium">{category.name}</span>
      </nav>

      <div className="mb-10">
        <h1 data-testid="category-title" className="text-4xl sm:text-5xl font-serif font-medium tracking-tight mb-3">{category.name}</h1>
        <p className="text-muted-foreground text-base">Explore fresh, organic {category.name.toLowerCase()} from our trusted farmers.</p>
      </div>

      {/* Subcategories */}
      {(category.subcategories || []).length > 0 && (
        <div className="mb-12">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">Shop by type</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {category.subcategories.map((sub) => (
              <Link key={sub.slug} to={`/products?category=${slug}&subcategory=${sub.slug}`} data-testid={`subcategory-card-${sub.slug}`}
                className="group relative overflow-hidden rounded-3xl aspect-[4/3] bg-card border border-border/50 shadow-sm">
                <img src={sub.image} alt={sub.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="text-white font-serif font-medium text-base">{sub.name}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* All products in this category */}
      <section className="mb-16">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="text-xl sm:text-2xl font-serif font-medium">All {category.name}</h2>
          <span className="text-xs text-muted-foreground">{products.length} {products.length === 1 ? 'product' : 'products'}</span>
        </div>
        {products.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-10">No products available yet in this category.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
            {products.map((p) => <ProductCard key={p.product_id} product={p} />)}
          </div>
        )}
      </section>

      {/* Basket Questions FAQ (category-specific) */}
      {basketFaqs.length > 0 && (
        <section data-testid="basket-faq-section" className="max-w-3xl mx-auto mt-16">
          <div className="text-center mb-6">
            <HelpCircle className="h-6 w-6 mx-auto text-primary mb-2" />
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Basket Questions</p>
            <h2 className="text-2xl font-serif font-medium">About {category.name}</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-2">
            {basketFaqs.map((f, i) => (
              <AccordionItem key={i} value={`bq-${i}`} data-testid={`basket-faq-${i}`} className="bg-card rounded-2xl border border-border/50 px-5">
                <AccordionTrigger className="text-sm font-medium text-left hover:no-underline">{f.question || f.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground whitespace-pre-wrap pb-5">{f.answer || f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      )}
    </div>
  );
}
